import { useLayoutEffect, useState } from 'react'

import { computeResumePreviewPageMetrics } from './resumePreviewPageLayout'

/** Horizontal padding on preview panel in wide layout. */
export const RESUME_DETAIL_PREVIEW_PAD_X = 96

/** Minimum editor column width. */
export const RESUME_DETAIL_EDITOR_MIN_PX = 260

/** Minimum preview column width when resizing. */
export const RESUME_DETAIL_PREVIEW_MIN_PX = 280

/** localStorage key for user-resized editor width (px). */
export const RESUME_DETAIL_EDITOR_WIDTH_STORAGE_KEY = 'genufit.resume-detail.editor-width-px'

/** Reserve px so sheet border/shadow is not clipped. */
export const RESUME_PREVIEW_FIT_SAFETY_PX = 4

/** Panel width that fits one A4 sheet at 1:1 plus side padding. */
export function resumeDetailNaturalPreviewWidthPx(
  pageWidthPx = computeResumePreviewPageMetrics().pageWidthPx
): number {
  return Math.round(pageWidthPx + RESUME_DETAIL_PREVIEW_PAD_X)
}

export type ResumeDetailSplitLayout = {
  splitWidth: number
  previewPaddingPx: number
}

export function resolveResumeDetailSplitLayout(
  splitWidthPx: number,
  naturalPreviewPx: number
): ResumeDetailSplitLayout {
  const wide = splitWidthPx >= naturalPreviewPx + RESUME_DETAIL_EDITOR_MIN_PX
  return {
    splitWidth: splitWidthPx,
    previewPaddingPx: wide ? 24 : 16,
  }
}

export function computeResumePreviewFitScale(availableWidthPx: number, pageWidthPx: number): number {
  if (availableWidthPx <= 0 || pageWidthPx <= 0) return 1
  const safe = Math.max(0, availableWidthPx - RESUME_PREVIEW_FIT_SAFETY_PX)
  return Math.min(1, safe / pageWidthPx)
}

/** Clip box width for scaled preview — ceil avoids subpixel floor clipping the right page edge. */
export function computeResumePreviewFitBoxWidthPx(pageWidthPx: number, scale: number): number {
  if (pageWidthPx <= 0 || scale <= 0) return 0
  return Math.ceil(pageWidthPx * scale)
}

/** Clip box height for scaled preview — ceil avoids clipping the bottom page edge. */
export function computeResumePreviewFitBoxHeightPx(contentHeightPx: number, scale: number): number {
  if (contentHeightPx <= 0 || scale <= 0) return 0
  return Math.ceil(contentHeightPx * scale)
}

export function useResumeDetailSplitLayout(
  splitRef: React.RefObject<HTMLDivElement | null>,
  naturalPreviewPx: number
): ResumeDetailSplitLayout {
  const [splitWidth, setSplitWidth] = useState(0)

  useLayoutEffect(() => {
    const el = splitRef.current
    if (!el) return

    const update = () => setSplitWidth(el.clientWidth)

    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [splitRef])

  return resolveResumeDetailSplitLayout(splitWidth, naturalPreviewPx)
}
